import { useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { getTermsStatus } from 'api/terms'
import { ACCOUNT_SCOPE_QUERY_KEY } from 'configs/queryClient'
import { useAuth } from 'contexts/AuthContext'
import useMessageStore from 'store/useMessageStore'

export const TERMS_STATUS_QUERY_KEY = 'terms-status'

// 同意は組織ではなく本人単位なので Account スコープに置き、組織の切り替えで破棄しない
export const termsStatusQueryKey = (uid: string | undefined) =>
  [ACCOUNT_SCOPE_QUERY_KEY, TERMS_STATUS_QUERY_KEY, uid] as const

const isTermsStatusQueryKey = (queryKey: readonly unknown[]) =>
  queryKey[0] === ACCOUNT_SCOPE_QUERY_KEY && queryKey[1] === TERMS_STATUS_QUERY_KEY

/** 表示中のタブでの同意状態の定期確認間隔（タブが非表示の間は止まる） */
export const TERMS_STATUS_RECHECK_INTERVAL_MS = 60_000

/**
 * 本人の利用規約の同意状態。通常ログイン・直リンク・再読込・既存セッションの復元の全経路で
 * RequireAuth がこれを見て業務画面に入れるかを決める。画面復帰時と表示中の定期確認で、
 * ログイン済みのまま同意必須ON・規約改定になった場合も同意画面へ案内する。
 */
export function useTermsStatus() {
  const { user, loading } = useAuth()

  return useQuery({
    queryKey: termsStatusQueryKey(user?.uid),
    // キーの UID と取得に使うトークンの利用者を一致させる（切り替え中の取り違えを防ぐ）
    queryFn: ({ queryKey: [, , uid] }) => getTermsStatus(uid),
    enabled: !loading && Boolean(user?.uid),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: TERMS_STATUS_RECHECK_INTERVAL_MS,
    retry: 1,
  })
}

/**
 * キャッシュに頼らず本人の同意状態を取り直す（取得した状態でキャッシュも更新する）。
 * 同意の送信結果が未確認のときなど、送信より後の状態で判断したい場合に使う。
 */
export const fetchLatestTermsStatus = (queryClient: QueryClient, uid: string) =>
  queryClient.fetchQuery({
    queryKey: termsStatusQueryKey(uid),
    queryFn: () => getTermsStatus(uid),
    staleTime: 0,
  })

/**
 * 業務利用が止まった（同意待ち・ログアウト）ときに、顧客データのキャッシュを破棄する。
 * 同意状態のクエリだけは残し、同意画面の判定に使う。
 */
export function clearBusinessCaches(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => !isTermsStatusQueryKey(query.queryKey),
  })
  useMessageStore.setState({ unreadCount: {}, adminAccounts: {} })
}
